"use client";
import {
  Braces,
  ChevronRight,
  FileCode2,
  PackageSearch,
  Search,
  Zap,
} from "lucide-react";
import {
  useState,
} from "react";
import {
  type ClassInfo,
  type ScanReport,
} from "../../../lib/types";


export function ClassesView({
  report,
  onOpenClass,
}: {
  report: ScanReport;
  onOpenClass: (item: ClassInfo) => void;
}) {
  const [query, setQuery] = useState("");
  const [onlySuspicious, setOnlySuspicious] = useState(false);
  const classes = report.classes.filter(
    (item) =>
      (!onlySuspicious || item.suspiciousReferences > 0) &&
      (!query ||
        `${item.name} ${item.superClass}`
          .toLowerCase()
          .includes(query.toLowerCase())),
  );

  return (
    <div className="view-stack">
      <section className="metrics-row class-metrics">
        <div>
          <span className="metric-icon">
            <Braces size={18} />
          </span>
          <span>
            <small>Parsed classes</small>
            <strong>{report.classes.length.toLocaleString()}</strong>
          </span>
        </div>
        <div>
          <span className="metric-icon danger">
            <Zap size={18} />
          </span>
          <span>
            <small>Suspicious references</small>
            <strong>
              {report.classes.reduce(
                (sum, item) => sum + item.suspiciousReferences,
                0,
              )}
            </strong>
          </span>
        </div>
        <div>
          <span className="metric-icon">
            <PackageSearch size={18} />
          </span>
          <span>
            <small>Packages</small>
            <strong>{new Set(report.classes.map((item) => item.packageName)).size}</strong>
          </span>
        </div>
      </section>
      <section className="panel table-panel">
        <div className="table-toolbar">
          <div className="search-field">
            <Search size={15} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search class or superclass"
              aria-label="Search classes"
            />
          </div>
          <label className="check-control">
            <input
              type="checkbox"
              checked={onlySuspicious}
              onChange={(event) => setOnlySuspicious(event.target.checked)}
            />
            <span />
            Suspicious references only
          </label>
        </div>
        <div className="data-table class-table">
          <div className="data-table-head">
            <span>Class</span>
            <span>Extends</span>
            <span>Bytecode</span>
            <span>Methods</span>
            <span>Signals</span>
            <span />
          </div>
          {classes.map((item) => (
            <button
              type="button"
              className="data-table-row"
              key={item.path}
              onClick={() => onOpenClass(item)}
            >
              <span className="class-name">
                <FileCode2 size={15} />
                <span>
                  <strong>{item.simpleName}</strong>
                  <small>{item.packageName}</small>
                </span>
              </span>
              <code>{item.superClass || "—"}</code>
              <span>{item.javaVersion}</span>
              <span>{item.methods.length}</span>
              <span>
                {item.suspiciousReferences ? (
                  <span className="signal-count">
                    {item.suspiciousReferences}
                  </span>
                ) : (
                  <span className="muted-dash">—</span>
                )}
              </span>
              <ChevronRight size={14} />
            </button>
          ))}
        </div>
        {!classes.length && (
          <div className="empty-panel">
            <Braces size={26} />
            <strong>No matching classes</strong>
            <span>Clear the filters or inspect the raw archive.</span>
          </div>
        )}
      </section>
    </div>
  );
}
